import { useState, useEffect, useRef } from "react";
import {
  messageIcon,
  searchIcon,
} from "../../../utility-functions/assetLoader";
import classes from "./AllChats.module.css";
import { JService } from "../../api-service/ApiService";
import { enqueueSnackbar } from "notistack";
import Contact from "../Components/Contact";
import { PhoneNumberUtil } from "google-libphonenumber";
import { useDebounce } from "../../../utility-functions/useDebounce";
import EditContactModal from "../Modals/EditContactModal";
import { useDispatch, useSelector } from "react-redux";
import AnimationPage from "../../shared/animation-page/AnimationPage";
import {
  addContactedUsers,
  setContactedUsers,
} from "../../features/whatsapp-contacts/whatsappContacts";
import { format, isToday, parse, isYesterday } from "date-fns";

//Helper function
const getTime = (datetime) => {
  if (!datetime) return;
  const parsed = parse(datetime, "dd-MM-yyyy hh:mm:ss a", new Date());
  if (isToday(parsed)) {
    return format(parsed, "hh:mm a");
  }
  if (isYesterday(parsed)) {
    return "Yesterday";
  }
  return format(parsed, "dd/MM/yy");
};

const AllChats = ({ getOpenedChatDetails }) => {
  const dispatch = useDispatch();

  const { userIds, usersById } = useSelector(
    (state) => state?.whatsappContacts
  );
  const contactedUsers = userIds?.map((id) => usersById[id]);

  const phoneNumberId = useSelector(
    (state) =>
      state?.whatsappBusiness?.value?.phone_numbers?.[0]?.phone_number_id
  );
  const phoneUtil = PhoneNumberUtil.getInstance();

  //Refs
  const containerRef = useRef(null);

  // States
  const [searchTerm, setSearchTerm] = useState("");
  const [searchedContacts, setSearchedContacts] = useState({
    contacted: [],
    others: [],
  });
  const [currentChat, setCurrentChat] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [nextUrl, setNextUrl] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(false);

  // Debounced search term
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // Handlers
  const getAllContactedUsers = async (url) => {
    try {
      setIsDataLoading(true);
      const baseUrl = `/api/v1/whatsapp/get-whatsapp-users/?phone_number_id=${phoneNumberId}&page=1`;

      const response = await JService.get(url || baseUrl);

      if (!response?.success)
        throw new Error(
          response?.message || "Could not retrieve contacted users"
        );
      const { next, results } = response.data;

      if (!url) {
        dispatch(setContactedUsers(results));
      } else {
        if (results?.length > 0) {
          dispatch(addContactedUsers(results));
        }
      }
      setNextUrl(next);
    } catch (error) {
      enqueueSnackbar(error?.message || "Could not retrieve contacted users", {
        variant: "error",
      });
    } finally {
      setIsDataLoading(false);
    }
  };

  const searchOtherContacts = async () => {
    try {
      setIsDataLoading(true);
      const _searchTerm = debouncedSearchTerm.replace("+", "");
      if (!_searchTerm) return;
      const queryParam = new URLSearchParams({
        search: _searchTerm,
      });

      const url = `api/v1/core/search-contact/?${queryParam}`;
      const response = await JService.get(url);
      if (!response?.success)
        throw new Error(
          response?.message ||
            `Could not retrieve contacts matching ${debouncedSearchTerm}`
        );
      if (!response?.data || !Array.isArray(response.data)) return;

      const filteredOthers =
        response?.data
          ?.filter(
            (other) =>
              !contactedUsers.some(
                (contacted) => contacted.number === other.number
              )
          )
          .map((c) => ({
            id: c.id,
            name: c.name,
            number: c.number,
            country_code: c.loc__country_code,
          })) || [];
      setSearchedContacts((prev) => ({
        ...prev,
        others: filteredOthers,
      }));
    } catch (error) {
      enqueueSnackbar(error?.message, { variant: "error" });
    } finally {
      setIsDataLoading(false);
    }
  };

  const filterContactedUsers = () => {
    setSearchedContacts((prev) => ({
      ...prev,
      contacted:
        contactedUsers?.filter(
          (c) =>
            c?.name
              ?.toLowerCase()
              ?.includes(debouncedSearchTerm.toLowerCase()) ||
            (c?.country_code + c?.number)?.includes(debouncedSearchTerm)
        ) || [],
    }));
  };

  const openChatHandler = (c) => {
    getOpenedChatDetails(c);
    setCurrentChat(c?.number);
  };

  const isValidPhoneNumber = (phoneNo) => {
    try {
      const numObj = phoneUtil.parse(phoneNo);
      return phoneUtil.isValidNumber(numObj);
    } catch (e) {
      console.log("is valid phone number error in wahtsapp all chats=", e);
      return false;
    }
  };

  const loadNextContactedUsers = () => {
    if (isLoadingMore || !nextUrl) return;
    setIsLoadingMore(true);
    getAllContactedUsers(nextUrl).finally(() => setIsLoadingMore(false));
  };

  const scrollHandler = () => {
    const el = containerRef.current;
    if (!el) return;

    const { scrollTop, clientHeight, scrollHeight } = el;
    if (scrollTop + clientHeight >= scrollHeight) {
      loadNextContactedUsers();
    }
  };

  const addContactHandler = async (name, number) => {
    try {
      const url = "/api/v1/whatsapp/manage-contact/",
        payload = { name: name, number: number };
      const response = await JService.post(url, payload);
      if (!response.success)
        throw new Error(response?.message || "Failed to add contact");

      enqueueSnackbar(response?.message || "Contact added successfully", {
        variant: "success",
      });
      getAllContactedUsers();
      setSearchTerm("");
    } catch (error) {
      enqueueSnackbar(error?.message, { variant: "error" });
    } finally {
      setShowAddModal(false);
    }
  };

  // Effects
  useEffect(() => {
    if (phoneNumberId) {
      getAllContactedUsers();
    }
  }, [phoneNumberId]);

  useEffect(() => {
    if (debouncedSearchTerm.trim() !== "") {
      filterContactedUsers();
      searchOtherContacts();
    }
  }, [debouncedSearchTerm]);

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener("scroll", scrollHandler);
      return () => container.removeEventListener("scroll", scrollHandler);
    }
  }, [nextUrl, isLoadingMore]);

  return (
    <div className={classes.container}>
      <div className={classes.searchBar}>
        {searchIcon()}
        <input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          type="search"
          placeholder="Search by name or number"
        />
      </div>
      <div className={classes.sep} />
      <div ref={containerRef} className={classes.content}>
        {debouncedSearchTerm.trim().length > 0 &&
        !isDataLoading &&
        searchedContacts?.contacted?.length === 0 &&
        searchedContacts?.others?.length === 0 ? (
          isValidPhoneNumber(debouncedSearchTerm) ? (
            <>
              <h6 className={classes.searchHeading}>Not in your contacts</h6>
              <Contact
                onClick={() => setShowAddModal(true)}
                unknownContact={true}
                phoneNo={debouncedSearchTerm}
              />
              {showAddModal ? (
                <EditContactModal
                  type="add"
                  visible={showAddModal}
                  closeModalHandler={() => setShowAddModal(false)}
                  phoneNumber={debouncedSearchTerm.replace(/[^\d+]/g, "")}
                  saveContactHandler={addContactHandler}
                />
              ) : (
                ""
              )}
            </>
          ) : (
            <div className={classes.noSearchResults}>
              <span>No search results</span>
            </div>
          )
        ) : (
          ""
        )}
        {debouncedSearchTerm && isDataLoading ? (
          <AnimationPage style={{ transform: "none" }} />
        ) : (
          <>
            {debouncedSearchTerm && searchedContacts?.contacted?.length > 0 && (
              <>
                <h6 className={classes.searchHeading}>Chats</h6>{" "}
                {searchedContacts?.contacted?.map((c) => (
                  <Contact
                    key={c?.id}
                    name={c?.name}
                    phoneNo={c?.country_code + " " + c?.number}
                    otherContact={false}
                    onClick={() => openChatHandler(c)}
                    message={c?.latest_message?.message}
                    time={getTime(c?.latest_message?.datetime)}
                    readStatus={c?.latest_message?.status}
                    messagesUnread={c?.unread_messages_count}
                    current={currentChat === c?.number}
                  />
                ))}
              </>
            )}
            {debouncedSearchTerm && searchedContacts?.others?.length > 0 && (
              <>
                <h6 className={classes.searchHeading}>
                  {contactedUsers?.length === 0 ? "Contacts" : "Other contacts"}
                </h6>
                {searchedContacts?.others?.map((c) => (
                  <Contact
                    key={c?.id}
                    name={c?.name}
                    phoneNo={c?.country_code + " " + c?.number}
                    otherContact={true}
                    onClick={() => openChatHandler(c)}
                    current={currentChat === c?.number}
                  />
                ))}
              </>
            )}
          </>
        )}
        {debouncedSearchTerm?.trim() === "" && isDataLoading ? (
          <AnimationPage style={{ transform: "none" }} />
        ) : (
          debouncedSearchTerm?.trim() === "" &&
          (contactedUsers?.length === 0 ? (
            <div className={classes.empty}>
              <div>{messageIcon("#3F3F46", 32, 32)}</div>
              <h6>No chats</h6>
              <p>Search contact to initiate conversation.</p>
            </div>
          ) : (
            contactedUsers?.map((c) => (
              <Contact
                key={c?.id}
                name={c?.name}
                phoneNo={c?.country_code + " " + c?.number}
                otherContact={false}
                onClick={() => openChatHandler(c)}
                message={c?.latest_message?.message}
                time={getTime(c?.latest_message?.datetime)}
                readStatus={c?.latest_message?.status}
                messagesUnread={c?.unread_messages_count}
                current={currentChat === c?.number}
              />
            ))
          ))
        )}
      </div>
    </div>
  );
};

export default AllChats;
